import {useRef} from 'react'
import {useMsal} from '@azure/msal-react'
import {loginRequest} from '../../Auth/auth-config'

const useFetchInterceptor = (config = {}) => {
  const {instance, accounts, inProgress} = useMsal()
  const {baseURL = '', defaultHeaders = {}} = config
  const tokenRef = useRef(null)
  const acquireToken = async () => {
    if (accounts?.[0] && inProgress === 'none') {
      try {
        const response = await instance.acquireTokenSilent({
          account: accounts[0],
          scopes: [
            process.env.NODE_ENV === 'production' ? globalThis.__env.REACT_APP_SCOPES : process.env.REACT_APP_SCOPES
          ]
        })
        tokenRef.current = response.accessToken
      } catch (error) {
        console.error('Failed to acquire token:', error)
        tokenRef.current = null
      }
    } else if (!accounts?.[0] && inProgress === 'none') {
      instance.acquireTokenRedirect(loginRequest)
    }
  }

  const requestInterceptor = (url, options = {}) => {
    const enhancedOptions = {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...defaultHeaders,
        ...options.headers
      }
    }
    if (tokenRef.current) {
      enhancedOptions.headers.Authorization = `Bearer ${tokenRef.current}`
    }
    const fullUrl = url.startsWith('http') ? url : `${baseURL}${url}`
    return {url: fullUrl, options: enhancedOptions}
  }

  const enhancedFetch = async (url, options = {}) => {
    // Get fresh token before each request
    await acquireToken()

    const {url: interceptedUrl, options: interceptedOptions} = requestInterceptor(url, options)
    const response = await fetch(interceptedUrl, interceptedOptions)
    if (!response.ok) {
      const error = new Error(`HTTP ${response.status}: ${response.statusText}`)
      error.status = response.status
      error.response = response
      throw error
    }
    return response
  }

  const get = (url, options = {}) => enhancedFetch(url, {method: 'GET', ...options})

  const post = (url, data, options = {}) => {
    return enhancedFetch(url, {
      method: 'POST',
      body: JSON.stringify(data),
      ...options
    })
  }

  return {
    fetch: enhancedFetch,
    get,
    post,
    hasToken: () => !!tokenRef.current
  }
}

export default useFetchInterceptor

/**
 * List of functions and their purpose:
 *
 * - useFetchInterceptor: Main hook that provides enhanced fetch with automatic MSAL token injection
 * - requestInterceptor: Adds auth token and base URL to requests before sending
 * - enhancedFetch: Core fetch function with token injection and error handling
 * - get: Convenience method for GET requests
 * - post: Convenience method for POST requests with JSON body
 * - hasToken: Returns boolean indicating if auth token is available
 */
